import React from 'react';
import { motion } from "framer-motion";
import { Lock, ArrowRight, X, Server, ShieldCheck, Zap } from "lucide-react";
import axios from 'axios';

export default function PaywallModal({ onClose, userEmail }) {
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      data-testid="paywall-modal"
    >
      {/* Blurred backdrop */}
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-md"
        onClick={onClose}
      />

      {/* Modal Card */}
      <motion.div
        initial={{ opacity: 0, scale: 0.92, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 w-full max-w-md"
      >
        <div
          className="rounded-[2.5rem] p-8 md:p-10 text-center relative overflow-hidden"
          style={{
            background: 'linear-gradient(145deg, rgba(17,19,40,0.98) 0%, rgba(11,13,23,0.99) 100%)',
            border: '1px solid rgba(124, 58, 237, 0.2)',
            boxShadow: '0 0 60px rgba(124, 58, 237, 0.1), 0 25px 50px rgba(0,0,0,0.5)',
          }}
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-6 right-6 p-2 rounded-full hover:bg-white/5 transition-colors"
            data-testid="paywall-close-btn"
          >
            <X className="w-5 h-5 text-white/20" />
          </button>

          {/* Icon Header */}
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-3xl bg-purple-500/10 border border-purple-500/15 mb-6">
            <Server className="w-8 h-8 text-purple-400" />
          </div>

          {/* Title & Badge */}
          <h2 className="font-bold text-3xl text-white tracking-tight mb-2">Audit Limit Reached</h2>
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-[10px] font-black uppercase tracking-widest mb-6">
            <Zap className="w-3 h-3 fill-current" /> High-Compute AI Audit
          </div>

          {/* THE TRANSPARENCY BLOCK (The "Why") */}
          <div className="bg-white/5 rounded-2xl border border-white/5 p-5 mb-8 text-left">
            <p className="text-sm text-slate-400 leading-relaxed italic">
              "To keep SiftCard <span className="text-white font-bold">100% unbiased</span>, we don't take commissions to push bad cards. High-end AI costs us real money to run—your ₹99 covers the server compute."
            </p>
          </div>

          {/* PRICING INFO */}
          <div className="flex justify-between items-center mb-8 px-2">
            <div className="text-left">
              <p className="text-[10px] uppercase tracking-widest text-white/30 font-bold">Lifetime Access</p>
              <p className="text-white/60 text-xs">Unlimited Audits Forever</p>
            </div>
            <div className="text-right">
              <span className="text-3xl font-black text-white">₹99</span>
            </div>
          </div>

          {/* SECURE RAZORPAY BUTTON */}
          <button
            className="group w-full py-4.5 bg-white text-black font-black rounded-2xl text-lg flex items-center justify-center gap-3 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] shadow-xl shadow-white/5"
            data-testid="paywall-unlock-btn"
            onClick={async () => {
              try {
                // 1. Create the Order on Railway
                const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/create-order`, {
                  email: userEmail 
                });
                
                const order = response.data;

                // 2. Open Razorpay Popup
                const options = {
                  key: process.env.REACT_APP_RAZORPAY_KEY_ID, 
                  amount: order.amount,
                  currency: order.currency,
                  name: "SiftCard Pro",
                  description: "Unlock Unbiased AI Audit",
                  order_id: order.id,
                  handler: async function (paymentResponse) {
                    // 3. Verify the payment
                    try {
                      const verifyRes = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/verify-payment`, {
                        razorpay_order_id: paymentResponse.razorpay_order_id,
                        razorpay_payment_id: paymentResponse.razorpay_payment_id,
                        razorpay_signature: paymentResponse.razorpay_signature,
                        email: userEmail
                      });

                      if (verifyRes.data.status === "success") {
                        alert("Access Granted! Unlocking your audit...");
                        window.location.reload(); 
                      }
                    } catch (err) {
                      alert("Verification failed. Please contact support@siftcard.in");
                    }
                  },
                  prefill: { email: userEmail },
                  theme: { color: "#7c3aed" },
                };

                const rzp = new window.Razorpay(options);
                rzp.open();

              } catch (error) {
                console.error("Payment Error:", error);
                alert("Could not start payment. Check your connection.");
              }
            }}
          >
            Unlock & Support the AI
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </button>

          {/* TRUST FOOTER */}
          <div className="flex items-center justify-center gap-4 mt-8 opacity-20">
            <div className="flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-widest text-white">
              <ShieldCheck className="w-3 h-3" /> Secure Payment
            </div>
            <div className="flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-widest text-white">
              <Lock className="w-3 h-3" /> Encrypted
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
